/**
 * Period math mirrors `apps/backend-strapi/src/utils/executiveReport/canonicalPeriod.js`.
 * Keep both functions in sync.
 *
 * Generates the last 12 completed instances of a cadence (weekly /
 * monthly / quarterly) so the Test panel offers exactly the calendar
 * shapes the cron will emit — never a freeform range.
 *
 * All math is in UTC. Weeks are Monday-anchored to match the backend.
 */

import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, type Locale } from "date-fns";
import { fr, enUS } from "date-fns/locale";

export type PeriodCadence = "weekly" | "monthly" | "quarterly";

export interface PeriodOption {
  startDate: string;
  endDate: string;
}

export interface PeriodPickerProps {
  cadence: PeriodCadence;
  value: PeriodOption | null;
  onChange: (next: PeriodOption) => void;
}

const INSTANCE_COUNT = 12;

/** Most-recent-completed Monday (UTC, 00:00:00.000). */
const mostRecentCompletedMonday = (ref: Date): Date => {
  const day = ref.getUTCDay(); // 0=Sun..6=Sat
  const offsetToMonday = day === 0 ? 6 : day - 1;
  const thisMonday = new Date(
    Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate() - offsetToMonday),
  );
  // Previous completed week starts 7 days before THIS Monday.
  const lastMonday = new Date(thisMonday);
  lastMonday.setUTCDate(thisMonday.getUTCDate() - 7);
  return lastMonday;
};

const buildWeeklyOptions = (now: Date): PeriodOption[] => {
  const out: PeriodOption[] = [];
  const firstMonday = mostRecentCompletedMonday(now);
  for (let i = 0; i < INSTANCE_COUNT; i += 1) {
    const start = new Date(firstMonday);
    start.setUTCDate(firstMonday.getUTCDate() - i * 7);
    // end = next Monday minus 1 ms
    const nextMonday = new Date(start);
    nextMonday.setUTCDate(start.getUTCDate() + 7);
    const end = new Date(nextMonday.getTime() - 1);
    out.push({
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    });
  }
  return out;
};

const buildMonthlyOptions = (now: Date): PeriodOption[] => {
  const out: PeriodOption[] = [];
  // Previous completed month = month before the current one.
  const yearRef = now.getUTCFullYear();
  const monthRef = now.getUTCMonth() - 1; // 0-indexed; negative is fine for Date.UTC
  for (let i = 0; i < INSTANCE_COUNT; i += 1) {
    const start = new Date(Date.UTC(yearRef, monthRef - i, 1));
    const firstNextMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
    const end = new Date(firstNextMonth.getTime() - 1);
    out.push({
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    });
  }
  return out;
};

const buildQuarterlyOptions = (now: Date): PeriodOption[] => {
  const out: PeriodOption[] = [];
  const currentQuarter = Math.floor(now.getUTCMonth() / 3); // 0..3
  // Previous completed quarter = current minus 1, expressed as a month
  // index relative to the current year.
  const prevQuarterStartMonth = currentQuarter * 3 - 3;
  for (let i = 0; i < INSTANCE_COUNT; i += 1) {
    const start = new Date(Date.UTC(now.getUTCFullYear(), prevQuarterStartMonth - i * 3, 1));
    const firstNextQuarter = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 3, 1));
    const end = new Date(firstNextQuarter.getTime() - 1);
    out.push({
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    });
  }
  return out;
};

const buildOptions = (cadence: PeriodCadence, now: Date): PeriodOption[] => {
  if (cadence === "weekly") return buildWeeklyOptions(now);
  if (cadence === "monthly") return buildMonthlyOptions(now);
  return buildQuarterlyOptions(now);
};

/** Capitalize first char (locale-friendly enough for FR/EN month names). */
const capitalize = (s: string): string =>
  s.length === 0 ? s : s.charAt(0).toLocaleUpperCase() + s.slice(1);

const formatLabel = (
  option: PeriodOption,
  cadence: PeriodCadence,
  locale: Locale,
  language: string,
): string => {
  const start = new Date(option.startDate);
  const end = new Date(option.endDate);

  if (cadence === "monthly") {
    // "Mai 2026" / "May 2026"
    return capitalize(format(start, "LLLL yyyy", { locale }));
  }

  if (cadence === "quarterly") {
    const qNum = Math.floor(start.getUTCMonth() / 3) + 1;
    const prefix = language === "fr" ? "T" : "Q";
    return `${prefix}${qNum} ${start.getUTCFullYear()}`;
  }

  // weekly
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();

  if (language === "fr") {
    if (sameMonth && sameYear) {
      // "Semaine du 6 au 12 mai 2026"
      return `Semaine du ${start.getUTCDate()} au ${end.getUTCDate()} ${format(end, "LLLL yyyy", {
        locale,
      })}`;
    }
    // "Semaine du 28 avr. au 4 mai 2026"
    const fromMonth = format(start, "LLL", { locale });
    const toMonth = format(end, "LLL", { locale });
    return `Semaine du ${start.getUTCDate()} ${fromMonth} au ${end.getUTCDate()} ${toMonth} ${end.getUTCFullYear()}`;
  }

  // EN
  if (sameMonth && sameYear) {
    // "Week of May 6–12, 2026"
    const monthName = format(start, "LLLL", { locale });
    return `Week of ${monthName} ${start.getUTCDate()}–${end.getUTCDate()}, ${start.getUTCFullYear()}`;
  }
  // "Week of Apr 28 – May 4, 2026"
  const fromMonth = format(start, "LLL", { locale });
  const toMonth = format(end, "LLL", { locale });
  return `Week of ${fromMonth} ${start.getUTCDate()} – ${toMonth} ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
};

const PeriodPicker = ({ cadence, value, onChange }: PeriodPickerProps) => {
  const { i18n } = useTranslation();
  const language = i18n.language?.startsWith("fr") ? "fr" : "en";
  const locale = language === "fr" ? fr : enUS;

  const options = useMemo(() => buildOptions(cadence, new Date()), [cadence]);

  // Default selection: most recent completed instance, OR re-anchor if the
  // current value doesn't match any of the freshly-generated options (e.g.
  // cadence just changed).
  useEffect(() => {
    if (options.length === 0) return;
    const match = value ? options.find((o) => o.startDate === value.startDate) : null;
    if (!match) {
      onChange(options[0]);
    }
    // We intentionally omit `value` and `onChange` from deps to avoid
    // re-firing when the parent's state updates as a result of our call.
    // The cadence change is the meaningful trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadence, options]);

  return (
    <select
      value={value ? value.startDate : ""}
      onChange={(e) => {
        const picked = options.find((o) => o.startDate === e.target.value);
        if (picked) onChange(picked);
      }}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
    >
      {options.map((o) => (
        <option key={o.startDate} value={o.startDate}>
          {formatLabel(o, cadence, locale, language)}
        </option>
      ))}
    </select>
  );
};

export default PeriodPicker;
